import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { StringParam, useQueryParams } from 'use-query-params'

import {
  useAccountAccess,
  useRequiredParams,
  useReset2FAModal,
  useRedactAccountModal,
  type AccountModifyAction
} from '@percent/admin-dashboard/common/hooks'
import { usePermissions } from '@percent/admin-dashboard/common/hooks/usePermissions/usePermissions'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { ErrorView } from '@percent/admin-dashboard/common/components/errorView/ErrorView'
import { AccountListTable, AccountType, Account, Reset2FAModal } from '@percent/domain/iam'
import { useQueryList } from '@percent/utility'
import { SearchInput, Flex, Badge } from '@percent/lemonade'
import type { SectionItem, SectionProps } from '@percent/lemonade'
import { useAccountListSearch } from '@percent/admin-dashboard/app/accounts/utils/hooks/useAccountListSearch'
import { RemoveAccountModal } from '@percent/admin-dashboard/app/accounts/components/remove-account'
import { RedactAccountModal } from '@percent/admin-dashboard/app/accounts/components/redact-account'
import { ReactivateAccountModal } from '@percent/admin-dashboard/app/accounts/components/reactivate-account'
import { isDefined } from '@percent/admin-dashboard/common/utility'
import { MEMBER_PROFILE_VIEW_PERMISSIONS } from '@percent/admin-dashboard/app/memberProfile/memberProfileAccess'
import { useDeleteAccountModal } from '@percent/admin-dashboard/common/hooks/useDeleteAccountModal'
import { useReactivateAccountModal } from '@percent/admin-dashboard/common/hooks/useReactivateAccountModal'

export function Members() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id: organisationId } = useRequiredParams()
  const { accountService } = useServices()
  const { userHasPermissions } = usePermissions()
  const { canViewAccount, canModifyAccount } = useAccountAccess()
  const [queryParams, setQueryParams] = useQueryParams({
    search: StringParam
  })

  const [{ data, isLoading, totalResults, errorMessage, status }, { nextPage, previousPage, refresh }] = useQueryList(
    ['/accounts', organisationId],
    accountService.getAccounts,
    {
      organisationId,
      type: AccountType.organisation,
      ...(queryParams.search && { search: queryParams.search })
    }
  )

  const {
    selectedAccount: reset2FAAccount,
    isModalOpen: isReset2FAModalOpen,
    openModal: openReset2FAModal,
    closeModal: closeReset2FAModal,
    reset2FA
  } = useReset2FAModal({ onSuccess: refresh })

  const {
    selectedAccount: redactAccount,
    isModalOpen: isRedactModalOpen,
    openModal: openRedactModal,
    closeModal: closeRedactModal
  } = useRedactAccountModal()

  const {
    selectedAccount: deleteAccount,
    isModalOpen: isDeleteModalOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal
  } = useDeleteAccountModal()

  const {
    selectedAccount: reactivateAccount,
    isModalOpen: isReactivateModalOpen,
    openModal: openReactivateModal,
    closeModal: closeReactivateModal
  } = useReactivateAccountModal()

  const { searchValue, handleKeyChange, handleKeyPress, handleClearValue, searchInputStatusMessage } =
    useAccountListSearch({
      queryParams,
      setQueryParams
    })

  const menuItem = (key: string, label: string, onClick: () => void, visible = true): SectionItem | undefined =>
    visible ? { key, label, onClick } : undefined

  type MemberRow = Pick<
    Account,
    | 'id'
    | 'fullName'
    | 'email'
    | 'partnerId'
    | 'organisationId'
    | 'admin'
    | 'twoFaSecretVerifiedAt'
    | 'status'
    | 'redactedAt'
  >

  const can = (account: MemberRow, action: AccountModifyAction): boolean => canModifyAccount(account, action)

  // The profile route requires its own permission on top of seeing the account
  const canViewProfile = (account: MemberRow): boolean =>
    userHasPermissions(MEMBER_PROFILE_VIEW_PERMISSIONS.organisation) && canViewAccount(account)

  const getMenuItems = (account: MemberRow): SectionProps[] => [
    {
      items: [
        menuItem(
          'view-profile',
          t('memberProfile.viewProfile'),
          () => navigate(`/organizations/${organisationId}/members/${account.id}`),
          canViewProfile(account)
        )
      ].filter(isDefined)
    },
    {
      title: t('account.modify.menu.authentication'),
      items: [
        menuItem(
          'reset-2fa',
          t('button.reset2FA'),
          () => openReset2FAModal({ id: account.id, email: account.email, fullName: account.fullName ?? null }),
          can(account, 'can_reset_2fa') && !!account.twoFaSecretVerifiedAt
        )
      ].filter(isDefined)
    },
    {
      title: t('account.modify.menu.membership'),
      items: [
        menuItem(
          'remove-account',
          t('account.modify.remove.button'),
          () => openDeleteModal(account),
          can(account, 'can_delete') && account.status !== 'removed'
        ),
        menuItem(
          'reactivate-account',
          t('account.modify.reactivate.button'),
          () => openReactivateModal(account),
          can(account, 'can_reactivate') && account.status === 'removed' && !account.redactedAt
        ),
        menuItem(
          'redact-account',
          t('account.modify.redact.button'),
          () => openRedactModal({ id: account.id, email: account.email, fullName: account.fullName ?? null }),
          can(account, 'can_redact')
        )
      ].filter(isDefined)
    }
  ]

  if (errorMessage && status === 'error') {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <>
      <AccountListTable
        accounts={data}
        totalResults={totalResults}
        nextPage={nextPage}
        previousPage={previousPage}
        isLoading={isLoading}
        getMenuItems={getMenuItems}
        extraColumns={[
          {
            key: 'role',
            header: t('typography.role'),
            render: account => <Badge variant="default">{account.role?.name ?? t('typography.member')}</Badge>,
            width: '15%'
          }
        ]}
        actionsAndFilters={
          <Flex direction="row" gap="md" align="center" w="100%">
            <SearchInput
              placeholder={t('table.accountsSearchPlaceholder')}
              value={searchValue}
              onChange={handleKeyChange}
              onKeyDown={handleKeyPress}
              handleClearValue={handleClearValue}
              statusMessage={searchInputStatusMessage}
              status={searchInputStatusMessage ? 'danger' : 'default'}
              width="100%"
            />
          </Flex>
        }
      />
      {reset2FAAccount && (
        <Reset2FAModal
          account={reset2FAAccount}
          isOpen={isReset2FAModalOpen}
          onClose={closeReset2FAModal}
          onReset={reset2FA}
        />
      )}
      {deleteAccount && (
        <RemoveAccountModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onSuccess={refresh}
          account={deleteAccount}
        />
      )}
      {redactAccount && (
        <RedactAccountModal
          isOpen={isRedactModalOpen}
          onClose={closeRedactModal}
          onSuccess={refresh}
          account={redactAccount}
        />
      )}
      {reactivateAccount && (
        <ReactivateAccountModal
          isOpen={isReactivateModalOpen}
          onClose={closeReactivateModal}
          onSuccess={refresh}
          account={reactivateAccount}
        />
      )}
    </>
  )
}
